import { useCallback, useRef } from 'react'
import type { ExpressCheckoutPartialAddress } from '@stripe/stripe-js'
import { v4 as uuid } from 'uuid'
import { useProductType } from '../../services/ProductTypesManager'
import { useKeys } from '../../services/KeysProvider'
import { usePricing } from '../Pricing/PricingProvider'
import {
  calculateCheckoutSummary,
  calculateGroupCheckoutSummary,
} from '../Pricing/Pricing.api'
import useApi from '../../api/apiProvider'
import { useCartStoreOrNull } from '../../stores/cartStore/CartStoreProvider'
import { useCheckoutPromoInputs } from '../../features/cart/hooks/useCheckoutPromoInputs'
import {
  buildGroupOrders,
  buildCheckoutSummaryParams,
  buildGroupCheckoutSummaryParams,
} from '../Pricing/checkoutSummaryParams'
import { useCartDiscount } from '../../features/cart/hooks/useCartDiscount'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'
import { useCheckout } from './CheckoutContext'
import {
  shapeExpressCheckoutSummary,
  shapeExpressCheckoutGroupSummary,
} from './expressCheckoutPricing'

function toExpressCheckoutPricingAddress(
  stripeAddress: ExpressCheckoutPartialAddress
) {
  return {
    country: stripeAddress.country,
    state: stripeAddress.state,
    city: stripeAddress.city,
    zipCode: stripeAddress.postal_code,
  }
}

export default function useExpressCheckoutPricingFetcher() {
  const { products, discountId, pricingSummary, isGroupOrder } = useCheckout()
  const { productType } = useProductType()
  const { pricingVariantsIds } = usePricing()
  const { promocode, giftCodes } = useCheckoutPromoInputs()
  const { ipCountry } = useKeys()
  const cartStore = useCartStoreOrNull()
  const api = useApi()
  const checkoutIdRef = useRef<string | undefined>(undefined)

  const pricingPayloads = useMobxSelector(
    () => cartStore?.getPricingPayloads() ?? [],
    []
  )

  const groupDiscountId = useCartDiscount()?.metadata?.discountId

  return useCallback(
    async (stripeAddress: ExpressCheckoutPartialAddress) => {
      const address = toExpressCheckoutPricingAddress(stripeAddress)
      const curExecutionId = uuid()

      const response = isGroupOrder
        ? await api.call(
            calculateGroupCheckoutSummary(
              buildGroupCheckoutSummaryParams({
                orders: buildGroupOrders(
                  pricingPayloads,
                  pricingVariantsIds,
                  groupDiscountId
                ),
                pricingCountry: ipCountry,
                checkoutId: checkoutIdRef.current,
                address,
                promocode,
                giftCodes,
                curExecutionId,
              })
            )
          )
        : await api.call(
            calculateCheckoutSummary(
              buildCheckoutSummaryParams({
                productType,
                pricingCountry: ipCountry,
                variantsIds: pricingVariantsIds,
                products,
                address,
                checkoutId: checkoutIdRef.current,
                discountId,
                promocode,
                giftCodes,
                curExecutionId,
              })
            )
          )

      if (!response.checkoutId) {
        throw new Error(
          'Express checkout pricing response is missing checkoutId'
        )
      }
      checkoutIdRef.current = response.checkoutId

      const pricing = isGroupOrder
        ? shapeExpressCheckoutGroupSummary(response, pricingSummary)
        : shapeExpressCheckoutSummary({
            checkoutResults: response.checkoutResults,
            checkoutId: response.checkoutId,
            discountId,
            productType,
            originalProducts: products,
            promoCode: promocode || undefined,
          })
      return { pricing, checkoutId: response.checkoutId }
    },
    [
      isGroupOrder,
      api,
      pricingPayloads,
      pricingVariantsIds,
      groupDiscountId,
      ipCountry,
      promocode,
      giftCodes,
      productType,
      products,
      discountId,
      pricingSummary,
    ]
  )
}
